import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

// Saves are ACCOUNT-SPECIFIC and backed by the existing Supabase "Saved_Events"
// table (RLS scopes rows to the signed-in user). Logged-out users see zero saves
// and cannot save. A local mirror (roadrnr_saved) is kept ONLY for the signed-in
// user so features like saved-event reminders keep working; it is cleared on logout.
const KEY = "roadrnr_saved";

let _ids = []; // current signed-in user's saved event ids (module cache)
let _loadedFor = null; // user id the cache belongs to

function emit() {
  window.dispatchEvent(new Event("roadrnr-saved-change"));
}
function writeMirror(ids) {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}
function clearMirror() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

async function loadFor(userId) {
  const { data, error } = await supabase
    .from("Saved_Events")
    .select("event_id")
    .eq("user_id", userId);
  if (error) {
    // Surface the real cause (e.g. missing table privileges) instead of silently
    // showing zero saves. Keep whatever we had cached for this user.
    // eslint-disable-next-line no-console
    console.error("[RoadRnr] Saved_Events load failed:", error);
    throw error;
  }
  _ids = Array.isArray(data) ? data.map((r) => r.event_id).filter(Boolean) : [];
  _loadedFor = userId;
  writeMirror(_ids);
  emit();
  return _ids;
}

export function useSaved() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState(
    user && _loadedFor === user.id ? [..._ids] : []
  );

  useEffect(() => {
    const sync = () =>
      setSaved(user && _loadedFor === user.id ? [..._ids] : []);
    window.addEventListener("roadrnr-saved-change", sync);
    return () => window.removeEventListener("roadrnr-saved-change", sync);
  }, [user]);

  useEffect(() => {
    if (!user) {
      // Logged out: clear cache + local mirror so nothing leaks into the next session.
      _ids = [];
      _loadedFor = null;
      clearMirror();
      setSaved([]);
      return;
    }
    if (_loadedFor === user.id) {
      setSaved([..._ids]);
      return;
    }
    let alive = true;
    loadFor(user.id)
      .then((ids) => alive && setSaved([...ids]))
      .catch(() => alive && setSaved([]));
    return () => {
      alive = false;
    };
  }, [user]);

  const isSaved = useCallback((id) => saved.includes(id), [saved]);

  const toggle = useCallback(
    async (id) => {
      if (!user) {
        toast.error("Sign in to save events");
        navigate("/login");
        return;
      }
      const currently = _ids.includes(id);
      _ids = currently ? _ids.filter((x) => x !== id) : [..._ids, id];
      writeMirror(_ids);
      emit();
      // supabase-js resolves with { error } rather than throwing, so the result
      // MUST be checked — otherwise a failed save/unsave (e.g. missing table
      // privileges) is silently ignored and never persists across a refresh.
      const revert = () => {
        _ids = currently ? [..._ids, id] : _ids.filter((x) => x !== id);
        writeMirror(_ids);
        emit();
        toast.error("Could not update your saved events");
      };
      try {
        const { error } = currently
          ? await supabase
              .from("Saved_Events")
              .delete()
              .eq("user_id", user.id)
              .eq("event_id", id)
          : await supabase
              .from("Saved_Events")
              .insert({ user_id: user.id, event_id: id });
        if (error) {
          // eslint-disable-next-line no-console
          console.error("[RoadRnr] Saved_Events write failed:", error);
          revert();
        }
      } catch {
        revert();
      }
    },
    [user, navigate]
  );

  return { saved, isSaved, toggle };
}
